import type { ReadOnlyRibbonControlDefProps } from "./RibbonControlDefinitionProps";
import { getRibbonMailboxInfo } from "./getRibbonMailboxInfo";
import { isNativeReportingEnabled } from "owa-reporting";
import { getMailMenuItemShouldShow } from "owa-mail-filterable-menu-behavior";
import { type MenuItemType } from "owa-filterable-menu/lib/components/MenuItemType";
import { getSelectedTableView, tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault } from "owa-mail-list-store";
import type { MailboxInfo } from "owa-client-types";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import type { ClientItem } from "owa-mail-store";
import folderIdToName from "owa-session-store/lib/utils/folderIdToName";
import { isItemInFolder } from "./isItemInFolder";

/**
 * The unified inbox mailboxInfo is virtual and can't answer capability questions, so resolve
 * the selection's actual account and check whether it supports "Report phishing".
 *
 * Returns false when the selection spans multiple mailboxes or none can be resolved, so callers
 * fall back to Junk / Not Junk.
 */
export function isReportPhishingSupportedForUnifiedInboxSelection(props: ReadOnlyRibbonControlDefProps): boolean {
    const tableView = getSelectedTableView();
    if (!tableView) {
        return false;
    }
    
    try {
        const rowMailboxInfo = props.itemId?.mailboxInfo ??
            tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(getRibbonMailboxInfo(props.mailboxInfo), [...tableView.selectedRowKeys.keys()], tableView);
        // Gate on the resolved per-account mailbox: it must support native reporting and pass
        // the same folder/selection/sender-self/draft checks as the non-unified ribbon path.
        return (isNativeReportingEnabled(rowMailboxInfo) &&
            getMailMenuItemShouldShow(25, rowMailboxInfo));
    }
    catch {
        // Selection spans different mailboxes (or none resolved).
        return false;
    }
}

/**
 * Whether the JunkNotJunk control should act as "Not junk" (i.e. the user is in a Junk context).
 * An item without a parent folder is treated as not Junk to match the click handler.
 */
export function isJunkNotJunkInJunkContext(item: ClientItem | undefined, isPopout: boolean | undefined, mailboxInfo: MailboxInfo): boolean {
    const tableFolderName = () => folderIdToName(getSelectedTableView()?.tableQuery?.folderId ?? "");
    
    if (!isGlobalFeatureEnabled("tri-promote-not-junk")) {
        if (isPopout) {
            return !!item && isItemInFolder(item, ["junkemail"]);
        }
        if (isUnifiedInboxMailboxInfo(mailboxInfo)) {
            return item ? isItemInFolder(item, ["junkemail"]) : tableFolderName() === "junkemail";
        }
        return (!getMailMenuItemShouldShow(24, mailboxInfo) &&
            getMailMenuItemShouldShow(55, mailboxInfo));
    }
    
    if (item) {
        const parentFolderId = item.ParentFolderId?.Id;
        return !!parentFolderId && folderIdToName(parentFolderId) === "junkemail";
    }
    return !isPopout && tableFolderName() === "junkemail";
}
